import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

export interface MicroMetric {
  label: string;
  value: string;
}

export interface CENSProduct {
  id: string;
  pilar: string;
  modulo: string;
  title: string;
  explicacionExquisita: string;
  features: string[];
  roi: string;
  techBackground: string;
  mainIcon: string;
  microMetrics: MicroMetric[];
}

const CENS_SOLUTIONS_DATA: CENSProduct[] = [
  // --- DESARROLLO DE SOFTWARE A LA MEDIDA ---
  {
    id: 'agpe-1',
    pilar: 'Desarrollo de software a la medida',
    modulo: 'Autogenerador a pequeña escala (AGPE)',
    title: 'Gestión Integral AGPE - Conexión Simplificada',
    explicacionExquisita: 'Plataforma diseñada para orquestar de punta a punta el trámite de solicitudes de conexión para Autogeneradores a Pequeña Escala (AGPE). La interfaz centraliza la tipificación de generación, reduciendo drásticamente la fricción administrativa que enfrentan las electrificadoras bajo la regulación de la CREG. Resuelve el dolor operativo asociado a la lentitud en la validación de documentación comercial y la dispersión de los datos del suscriptor.',
    features: [
      'Centralización de datos del suscriptor e inmueble.',
      'Auditoría y trazabilidad en validación de documentos CREG.',
      'Administración ágil de devoluciones de excedentes.'
    ],
    roi: 'Reducción del 45% en tiempos de aprobación',
    techBackground: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?q=80&w=800', // Paneles Solares/Energia Renovable
    mainIcon: 'fas fa-solar-panel',
    microMetrics: [
      { label: 'Tiempo de Flujo', value: '0.8s' },
      { label: 'Integridad', value: '100%' }
    ]
  },
  {
    id: 'ami-1',
    pilar: 'Desarrollo de software a la medida',
    modulo: 'Infraestructura de Medición Avanzada (AMI)',
    title: 'Monitoreo Avanzado AMI - Perfiles de Carga',
    explicacionExquisita: 'Ecosistema de software integrado para lectura, análisis y telegestión de medidores bidireccionales. Supera las limitaciones de recolección manual de datos, brindando a CENS una visibilidad en tiempo real de los perfiles de carga, consumos históricos y balances. Este módulo permite mitigar pérdidas no técnicas detectando patrones anómalos de manera proactiva, y facilita la interoperabilidad con sistemas de facturación.',
    features: [
      'Análisis de matrices y gráficas de consumo en tiempo real.',
      'Telegestión bidireccional segura con medidores de campo.',
      'Detección automática de desviaciones y posibles fraudes.'
    ],
    roi: 'Control proactivo de pérdidas no técnicas',
    techBackground: 'https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=800', // Circuitos/Sensores
    mainIcon: 'fas fa-microchip',
    microMetrics: [
      { label: 'Lectura Remota', value: '15 min' },
      { label: 'Reducción Fallas', value: '98%' }
    ]
  },
  {
    id: 'aie-1',
    pilar: 'Desarrollo de software a la medida',
    modulo: 'Portal de Alquiler de Infraestructura Eléctrica (AIE)',
    title: 'Portal AIE - Gestión de Factibilidad',
    explicacionExquisita: 'Solución web dedicada a la administración de solicitudes para el uso de postería y ductería (Alquiler de Infraestructura Eléctrica). Automatiza el flujo de trabajo desde la solicitud de factibilidad hasta la ejecución del proyecto, eliminando el uso de hojas de cálculo y correos aislados. Permite a los ingenieros de planeación de CENS evaluar capacidades de red, adjuntar soportes y aprobar intervenciones de terceros con máxima eficiencia.',
    features: [
      'Flujos de aprobación digitalizados para factibilidad y ejecución.',
      'Trazabilidad completa de anexos e información de proyectos.',
      'Transparencia y consulta de estado de trámite para terceros.'
    ],
    roi: 'Optimización del 60% en gestión de terceros',
    techBackground: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=800', // Red global/Nodos
    mainIcon: 'fas fa-project-diagram',
    microMetrics: [
      { label: 'Control Apoyos', value: '100k+' },
      { label: 'Auditoría', value: 'Activa' }
    ]
  },
  {
    id: 'sig-1',
    pilar: 'Desarrollo de software a la medida',
    modulo: 'Mapa de Disponibilidad de la Red',
    title: 'SIG - Mapa de Disponibilidad de Transformadores',
    explicacionExquisita: 'Sistema de Información Georreferenciada (SIG) que expone visualmente el estado, carga y capacidad instalada de los activos de distribución (Transformadores, Subestaciones y Redes de Baja Tensión). Soluciona el problema de visibilidad táctica en la planeación operativa, permitiendo a CENS determinar instantáneamente si un sector cuenta con la disponibilidad técnica necesaria para soportar nuevas cargas o conexiones.',
    features: [
      'Mapeo interactivo de transformadores y subestaciones.',
      'Calculadora integrada de capacidades de red.',
      'Apoyo crítico en la toma de decisiones para expansión de red.'
    ],
    roi: 'Eficiencia total en planeación de expansión',
    techBackground: 'https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=800', // Mapa geoespacial abstracto
    mainIcon: 'fas fa-map-marked-alt',
    microMetrics: [
      { label: 'Render Geo', value: '0.2s' },
      { label: 'Precisión', value: 'Alta' }
    ]
  },
  {
    id: 'mdm-1',
    pilar: 'Desarrollo de software a la medida',
    modulo: 'Sistema de Gestión de Datos de Medición (MDM)',
    title: 'MDM - Balance de Transformadores',
    explicacionExquisita: 'Motor analítico para la gestión centralizada de datos de medición (Meter Data Management). Este módulo realiza el cruce entre la macro-medición en el transformador y la micro-medición de los usuarios asociados, calculando balances energéticos precisos. Su implementación es clave para que CENS ubique geográficamente las fugas de energía (pérdidas técnicas y no técnicas) a nivel de circuito y transformador.',
    features: [
      'Balances energéticos automatizados por transformador.',
      'Integración con datos de facturación (PPA Facturación).',
      'Visor centralizado y menús operacionales intuitivos.'
    ],
    roi: 'Reducción drástica de pérdidas no técnicas',
    techBackground: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=800', // Analitica/Data/Lines
    mainIcon: 'fas fa-chart-line',
    microMetrics: [
      { label: 'Macro vs Micro', value: 'Sincronizado' },
      { label: 'Desviaciones', value: 'Visibles' }
    ]
  },
  {
    id: 'gen-1',
    pilar: 'Desarrollo de software a la medida',
    modulo: 'Información de Generación',
    title: 'Desempeño de Sistemas de Generación',
    explicacionExquisita: 'Panel interactivo que detalla la información sobre el desempeño de los sistemas de generación eléctrica. Soluciona el problema de tener métricas aisladas ofreciendo a CENS una vista consolidada de los megavatios hora producidos, tiempos de inactividad, y la eficiencia general de sus plantas y autogeneradores.',
    features: [
      'Consolidación visual de métricas de generación.',
      'Reportes automatizados de rendimiento y eficiencia.',
      'Monitoreo centralizado para facilitar mantenimientos preventivos.'
    ],
    roi: 'Aumento del 25% en disponibilidad de datos operativos',
    techBackground: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?q=80&w=800', // Turbinas/Generación
    mainIcon: 'fas fa-industry',
    microMetrics: [
      { label: 'Uptime', value: '99.9%' },
      { label: 'KPIs', value: 'Real-Time' }
    ]
  },
  {
    id: 'eventos-1',
    pilar: 'Desarrollo de software a la medida',
    modulo: 'Sistema de Monitoreo de Medidores AMI',
    title: 'Monitoreo Estratégico - Mapa de Eventos',
    explicacionExquisita: 'Centro de control visual que monitorea en tiempo real las alarmas y eventos críticos provenientes de medidores, cuchillas, reconectadores y seccionadores. Permite a los operadores de red identificar fallas, maniobras no autorizadas e interrupciones del servicio al instante. La interfaz agrupa los eventos por categoría y nivel de severidad, acelerando el despacho de cuadrillas y mejorando los indicadores SAIDI y SAIFI de CENS.',
    features: [
      'Alerta temprana de fallas en medidores y reconectadores.',
      'Clasificación inteligente por categorías de criticidad.',
      'Impacto directo en la mejora de indicadores de calidad (SAIDI/SAIFI).'
    ],
    roi: 'Mejora continua en continuidad del servicio',
    techBackground: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=800', // Servidores/Control Room
    mainIcon: 'fas fa-satellite-dish',
    microMetrics: [
      { label: 'Latencia Eventos', value: '< 2s' },
      { label: 'SAIDI/SAIFI', value: 'Optimizado' }
    ]
  },

  // --- ADMINISTRACIÓN DE BASES DE DATOS ---
  {
    id: 'dwh-1',
    pilar: 'Administración y mantenimiento de bases de datos',
    modulo: 'Bodega de Datos',
    title: 'Arquitectura Medallion & Datawarehouse',
    explicacionExquisita: 'Implementación de arquitecturas de datos modernas (Medallion Architecture) para consolidar los silos de información de la electrificadora. Este pilar permite la integración fluida mediante procesos ETL/ELT complejos, transformando datos crudos operativos y comerciales en activos de información curados y listos para Business Intelligence. Garantiza gobernanza, velocidad de consulta y confiabilidad de los datos.',
    features: [
      'Catálogo de procesos ETL y procedimientos almacenados orquestados.',
      'Diseño de Datawarehouse altamente escalable.',
      'Modelado de datos para soporte a decisiones estratégicas.'
    ],
    roi: 'Datos gobernados y 100% confiables',
    techBackground: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=800', // Servidores abstractos
    mainIcon: 'fas fa-database',
    microMetrics: [
      { label: 'Ingesta', value: 'Petabytes' },
      { label: 'ACID', value: 'Cumple' }
    ]
  },

  // --- AUTOMATIZACIÓN DE PROCESOS ---
  {
    id: 'rpa-1',
    pilar: 'Automatización de procesos y RPA',
    modulo: 'Automatización de procesos',
    title: 'RPA en Operación y Servicio al Cliente',
    explicacionExquisita: 'Despliegue de agentes robóticos para ejecutar tareas repetitivas y de alto volumen, tales como la estructuración de respuestas a PQRs (Peticiones, Quejas y Reclamos) o la generación de documentación. Alivia la carga operativa del personal humano de CENS, eliminando errores de digitación y garantizando el cumplimiento de los tiempos regulatorios exigidos por la Superintendencia de Servicios Públicos.',
    features: [
      'Generación de plantillas de respuesta automatizadas (PQRs).',
      'Dashboards de control de bots y envíos DOD.',
      'Reducción a cero de errores operativos manuales.'
    ],
    roi: 'Cumplimiento normativo y eficiencia 24/7',
    techBackground: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?q=80&w=800', // Robotica abstracta
    mainIcon: 'fas fa-robot',
    microMetrics: [
      { label: 'Disponibilidad', value: '24/7' },
      { label: 'Margen de Error', value: '0%' }
    ]
  },
  {
    id: 'difusion-1',
    pilar: 'Automatización de procesos y RPA',
    modulo: 'Difusión y Monitoreo',
    title: 'Gestión Automática de Fallas y Notificaciones',
    explicacionExquisita: 'Sistema que detecta fallas, reparaciones y programas de mantenimiento en sectores específicos de la red, enviando automáticamente notificaciones a los usuarios afectados. Esto mejora drásticamente la percepción del servicio al mantener informado al cliente final sobre la anomalía y su pronta solución, mientras los ingenieros supervisan el estado de la red en tiempo real.',
    features: [
      'Detección automática de zonas y usuarios afectados.',
      'Notificaciones proactivas vía SMS o correo electrónico.',
      'Cuadro de mando para control y monitoreo en operación.'
    ],
    roi: 'Caída del 40% en llamadas de reclamo por cortes',
    techBackground: 'https://images.unsplash.com/photo-1517433670267-08bbd4be890f?q=80&w=800', // Comunicaciones
    mainIcon: 'fas fa-broadcast-tower',
    microMetrics: [
      { label: 'Notificación', value: 'Inmediata' },
      { label: 'Alcance', value: 'Masivo' }
    ]
  },

  // --- ECOSISTEMA VOZ DEL CLIENTE ---
  {
    id: 'voc-1',
    pilar: 'Ecosistema Voz del Cliente',
    modulo: 'VOC Indicadores - Tablero',
    title: 'VOC: Analítica de Voz del Cliente',
    explicacionExquisita: 'Ecosistema de tableros directivos que procesa e interpreta la satisfacción y el sentimiento de los usuarios de CENS a través de múltiples canales (encuestas, llamadas, redes). Mediante analítica de texto avanzada y procesamiento de lenguaje natural, el sistema identifica promotores y detractores, midiendo el NPS y ofreciendo inteligencia geoespacial para detectar zonas con alta insatisfacción del servicio.',
    features: [
      'Tableros de control gerencial con indicadores NPS y CSAT.',
      'Monitoreo del desempeño de agentes de servicio al cliente.',
      'Análisis geoespacial de quejas e inconformidades.'
    ],
    roi: 'Aumento significativo en satisfacción de usuarios',
    techBackground: 'https://images.unsplash.com/photo-1552581234-26160f608093?q=80&w=800', // Personas/Equipo
    mainIcon: 'fas fa-users-cog',
    microMetrics: [
      { label: 'NPS', value: 'Trackeado' },
      { label: 'Sentimiento', value: 'Analizado AI' }
    ]
  },
  {
    id: 'perfil-1',
    pilar: 'Ecosistema Voz del Cliente',
    modulo: 'Perfilamiento Clientes',
    title: 'Segmentación Inteligente y Scoring',
    explicacionExquisita: 'Algoritmos estadísticos y de Machine Learning que perfilan la base de suscriptores basándose en su historial de pagos, consumos, deuda e interacciones. Este módulo asigna automáticamente clústeres (Premium, Práctico, Vulnerable, Riesgo) permitiendo al área de cartera y servicio al cliente de CENS ejecutar campañas focalizadas y maximizar el recaudo sin fricción.',
    features: [
      'Clasificación automática de riesgo de pago y deserción.',
      'Segmentación visual interactiva para equipos no técnicos.',
      'Estrategias de recaudo proactivas basadas en datos predictivos.'
    ],
    roi: 'Optimización agresiva en gestión de cartera',
    techBackground: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?q=80&w=800', // Data segments
    mainIcon: 'fas fa-id-card-alt',
    microMetrics: [
      { label: 'Clustering', value: 'Dinámico' },
      { label: 'Modelos', value: 'AutoML' }
    ]
  },

  // --- FACTURACIÓN INTELIGENTE ---
  {
    id: 'fact-web-1',
    pilar: 'Facturación inteligente y personalizada',
    modulo: 'Infofactura Web',
    title: 'Dashboards Interactivos Infofactura',
    explicacionExquisita: 'La Infofactura Web ofrece un dashboard interactivo donde el usuario de CENS puede explorar sus tendencias de consumo de manera transparente. Desglosa los componentes tarifarios regulados, permitiendo a los clientes corporativos y residenciales analizar comparativas mensuales de su facturación, reduciendo la incertidumbre y el volumen de reclamaciones comerciales.',
    features: [
      'Plataforma web con comparativas de consumo histórico.',
      'Desglose transparente de componentes tarifarios.',
      'Empoderamiento del usuario frente a su propio gasto energético.'
    ],
    roi: 'Reducción de quejas por incomprensión de factura',
    techBackground: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800', // Dashboard web
    mainIcon: 'fas fa-file-invoice-dollar',
    microMetrics: [
      { label: 'Transparencia', value: 'Total' },
      { label: 'Carga', value: '< 1s' }
    ]
  },
  {
    id: 'video-fact-1',
    pilar: 'Facturación inteligente y personalizada',
    modulo: 'Videofactura',
    title: 'Experiencia Interactiva de Videofacturación',
    explicacionExquisita: 'Transformación del estático PDF en una experiencia digital dinámica. La Videofactura utiliza avatares generados por IA que explican los componentes del cobro en un lenguaje amigable y natural, complementado con gráficos visuales y voz. Permite a los suscriptores comprender fácilmente los cobros y subsidios aplicados en su periodo de consumo.',
    features: [
      'Explicación en video generado por IA sobre el recibo de luz.',
      'Clarificación de rubros complejos de la CREG.',
      'Interactividad y diseño envolvente adaptado al branding CENS.'
    ],
    roi: 'Mayor retención del mensaje y reducción de reclamos',
    techBackground: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=800', // Abstract IA Face
    mainIcon: 'fas fa-video',
    microMetrics: [
      { label: 'Avatar IA', value: 'Lip-sync 3D' },
      { label: 'Renderizado', value: 'Cloud' }
    ]
  },
  {
    id: 'lsc-1',
    pilar: 'Facturación inteligente y personalizada',
    modulo: 'Lenguaje de Señas Colombiana (LSC)',
    title: 'Inclusión con Avatar LSC',
    explicacionExquisita: 'Traductor virtual incrustado que convierte el contenido de los recibos y el portal web a Lengua de Señas Colombiana (LSC) en tiempo real. Elimina barreras de comunicación garantizando que los usuarios con discapacidad auditiva accedan de manera autónoma a la información de sus facturas, proyectando una imagen de marca inclusiva y responsable por parte de CENS.',
    features: [
      'Interpretación simultánea con avatar 3D.',
      'Accesibilidad universal sin requerir intermediarios.',
      'Alineación total con normativas de inclusión social.'
    ],
    roi: 'Compromiso social AAA comprobado',
    techBackground: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?q=80&w=800', // Manos/Señas
    mainIcon: 'fas fa-hands-helping',
    microMetrics: [
      { label: 'Inclusión', value: '100%' },
      { label: 'Accesibilidad', value: 'WCAG' }
    ]
  },

  // --- INTELIGENCIA ARTIFICIAL APLICADO A LA INDUSTRIA ---
  {
    id: 'ia-pred-1',
    pilar: 'Inteligencia artificial aplicado a la industria',
    modulo: 'Predicciones de Consumo de Energía',
    title: 'Forecasting Energético con IA',
    explicacionExquisita: 'Plataforma de machine learning que proyecta el comportamiento futuro de la demanda energética. Mediante el análisis de series de tiempo, datos históricos de consumo AMI y variables exógenas (clima, estacionalidad), el modelo predictivo anticipa picos de carga. Esto empodera a CENS para realizar compras de energía en bolsa más inteligentes y gestionar la demanda de forma eficiente.',
    features: [
      'Modelos predictivos de alta precisión para pronóstico de demanda.',
      'Integración con datos en tiempo real de operaciones.',
      'Apoyo fundamental en la estrategia de compra de energía.'
    ],
    roi: 'Ahorro millonario en compra y distribución',
    techBackground: 'https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?q=80&w=800', // Forecasting/Data
    mainIcon: 'fas fa-brain',
    microMetrics: [
      { label: 'Predicción', value: 'Series de Tiempo' },
      { label: 'RMSE', value: 'Mínimo' }
    ]
  },
  {
    id: 'ia-bot-1',
    pilar: 'Inteligencia artificial aplicado a la industria',
    modulo: 'Asistentes Virtuales',
    title: 'Asistentes Cognitivos Conversacionales',
    explicacionExquisita: 'Chatbots avanzados de IA diseñados para interactuar fluidamente a través de texto o voz. Superan las arquitecturas de árbol de decisión permitiendo a los clientes de CENS agendar citas, consultar daños reportados o iniciar PQRs. Integrable vía WhatsApp y web, liberando a los asesores humanos de consultas repetitivas de primer nivel.',
    features: [
      'NLU (Natural Language Understanding) profundo.',
      'Automatización de atención de primer nivel.',
      'Soporte omnicanal y disponibilidad permanente.'
    ],
    roi: 'Contención de hasta un 65% en volumen de Contact Center',
    techBackground: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?q=80&w=800', // Robot face
    mainIcon: 'fas fa-comments',
    microMetrics: [
      { label: 'NLU', value: 'Integrado' },
      { label: 'Omnicanal', value: 'WhatsApp/Web' }
    ]
  },
  {
    id: 'ia-ra-1',
    pilar: 'Inteligencia artificial aplicado a la industria',
    modulo: 'Entornos Simulados para Realidad Aumentada',
    title: 'Simulación de Operación con Realidad Aumentada',
    explicacionExquisita: 'Plataforma para entrenamiento y mantenimiento predictivo en campo empleando Realidad Aumentada. Los ingenieros y linieros de CENS pueden visualizar superposiciones técnicas de subestaciones y transformadores antes de intervenir físicamente, mitigando riesgos eléctricos graves y agilizando las curvas de aprendizaje operativo.',
    features: [
      'Capacitación inmersiva sin riesgo operativo.',
      'Asistencia remota avanzada para cuadrillas en campo.',
      'Superposición de esquemas eléctricos en equipos reales.'
    ],
    roi: 'Cero incidentes en entrenamiento y eficiencia táctica',
    techBackground: 'https://images.unsplash.com/photo-1617802690992-15d93263d3a9?q=80&w=800', // VR/AR Lens
    mainIcon: 'fas fa-vr-cardboard',
    microMetrics: [
      { label: 'FPS', value: '60' },
      { label: 'Render 3D', value: 'Tiempo Real' }
    ]
  },
  {
    id: 'ia-rag-1',
    pilar: 'Inteligencia artificial aplicado a la industria',
    modulo: 'Agentic RAG',
    title: 'Motores Autónomos Agentic RAG',
    explicacionExquisita: 'Evolución de la tecnología RAG que incorpora agentes de IA autónomos al proceso de recuperación de información. El agente planifica, itera y valida respuestas basadas en manuales técnicos o regulatorios CREG de CENS antes de contestar al equipo. Transforma la gestión de PQRS de un proceso manual y reactivo a un ecosistema proactivo.',
    features: [
      'Recuperación aumentada de generación autónoma.',
      'Iteración y validación cruzada con normativas vigentes.',
      'Libera al equipo de atención al cliente de validaciones manuales.'
    ],
    roi: 'Hiperautomatización del backoffice documental',
    techBackground: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?q=80&w=800', // AI abstract nodes
    mainIcon: 'fas fa-network-wired',
    microMetrics: [
      { label: 'Alucinaciones', value: '0.1%' },
      { label: 'Indexación', value: 'Semántica' }
    ]
  },
  {
    id: 'ia-agentes-1',
    pilar: 'Inteligencia artificial aplicado a la industria',
    modulo: 'Agentes IA',
    title: 'Fuerza Laboral Autónoma con IA',
    explicacionExquisita: 'Sistemas autónomos capaces de razonar, planificar, actuar y aprender. Estos agentes se conectan nativamente a los CRM o sistemas de facturación de CENS y resuelven por completo un trámite administrativo sin supervisión humana. Ejecutan y completan los procesos iterativamente buscando la resolución más eficiente.',
    features: [
      'Ejecución directa de acciones sobre ERP y CRM.',
      'Descomposición de tareas complejas.',
      'Aprendizaje y adaptación continua.'
    ],
    roi: 'Escalabilidad infinita sin incremento de nómina',
    techBackground: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?q=80&w=800', // Cyborg / abstract logic
    mainIcon: 'fas fa-user-astronaut',
    microMetrics: [
      { label: 'Acción', value: 'Autónoma' },
      { label: 'Razonamiento', value: 'Chain-of-Thought' }
    ]
  },
  {
    id: 'ia-ft-1',
    pilar: 'Inteligencia artificial aplicado a la industria',
    modulo: 'Fine-Tuning',
    title: 'Modelos de Lenguaje Afinables (Fine-Tuning)',
    explicacionExquisita: 'Personalización de modelos de lenguaje fundacionales con el conocimiento experto y la jerga interna de CENS. Convertimos modelos genéricos en arquitectos corporativos privados, garantizando que el entendimiento y generación de texto esté en sintonía absoluta con el negocio energético de EPM y la privacidad de sus datos.',
    features: [
      'Modelos entrenados con normativas y flujos propios de la electrificadora.',
      'Seguridad corporativa sin filtración de datos.',
      'Resolución experta y especializada.'
    ],
    roi: 'IA soberana e inteligente al servicio corporativo',
    techBackground: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?q=80&w=800', // Binary code/Matrix
    mainIcon: 'fas fa-sliders-h',
    microMetrics: [
      { label: 'Overfitting', value: 'Controlado' },
      { label: 'Context', value: '128k tokens' }
    ]
  }
];

@Component({
  selector: 'app-landing-cens',
  templateUrl: './landing-cens.component.html',
  styleUrls: ['./landing-cens.component.scss']
})
export class LandingCensComponent implements OnInit {

  mobileMenuOpen = false;

  public pilares: string[] = [];
  public pilarActivo: string = '';
  public productosFiltrados: CENSProduct[] = [];
  public productoActivo: CENSProduct | null = null;

  constructor(private router: Router) { }

  ngOnInit(): void {
    window.scrollTo(0, 0);
    const pilarSet = new Set(CENS_SOLUTIONS_DATA.map(p => p.pilar));
    this.pilares = Array.from(pilarSet);

    if (this.pilares.length > 0) {
      this.seleccionarPilar(this.pilares[0]);
    }
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  fn_goTToSection(sectionId: string): void {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  seleccionarPilar(pilar: string): void {
    this.pilarActivo = pilar;
    this.productosFiltrados = CENS_SOLUTIONS_DATA.filter(p => p.pilar === pilar);
    if (this.productosFiltrados.length > 0) {
      this.productoActivo = this.productosFiltrados[0];
    } else {
      this.productoActivo = null;
    }
  }

  seleccionarProducto(producto: CENSProduct): void {
    this.productoActivo = producto;
  }
}
